import moment from 'moment';

export interface CertificationGroup {
  effectiveDate: string;
  isExistingWorkerForGroup: boolean;
  workerBadgeFirstActivatedAt: string | null;
  id: string;
  name: string;
  validationType: number;
  certifications: Array<{
    id: string;
    alias: string | null;
    name: string;
  }>;
  exemptedCertifications: any[];
  existingWorkerMandatoryDeadline: string | null;
  isPresetDefault: boolean;
  existingWorkerGracePeriodInDays: number | null;
  newWorkerMandatoryDeadline: string | null;
  newWorkerGracePeriodInDays: number | null;
  createdAt: string;
  lastUpdatedAt: string;
}

export interface CertificationItem {
  id: string;
  certification: {
    id: string;
    name: string;
  };
}

export const getEffectiveDateText = (item: CertificationGroup, t: (key: string, params?: any) => string): string => {
  const {
    effectiveDate,
    isExistingWorkerForGroup,
    existingWorkerGracePeriodInDays,
    newWorkerGracePeriodInDays,
    existingWorkerMandatoryDeadline,
    newWorkerMandatoryDeadline,
    workerBadgeFirstActivatedAt,
  } = item;

  const isInfiniteEffectiveDate = new Date(effectiveDate)?.getFullYear() >= 9999;
  const gracePeriodInDays =
    (isExistingWorkerForGroup ? existingWorkerGracePeriodInDays : newWorkerGracePeriodInDays) || 0;

  const mandatoryDeadline =
    (isExistingWorkerForGroup ? existingWorkerMandatoryDeadline : newWorkerMandatoryDeadline) || null;

  if (!workerBadgeFirstActivatedAt && mandatoryDeadline) {
    if (gracePeriodInDays > 0) {
      return t('certifications.fcaGracePeriodPreactiveWorkerPeriodText', {
        gracePeriodInDays: gracePeriodInDays.toString(),
        date: moment(mandatoryDeadline).format('ll'),
      });
    } else {
      return t('certifications.fcaEffectiveFrom', {
        date: moment(mandatoryDeadline).format('ll'),
      });
    }
  }

  return isInfiniteEffectiveDate
    ? t('certifications.fcaGracePeriodText', {
        gracePeriodInDays: gracePeriodInDays.toString(),
      })
    : t('certifications.fcaEffectiveFrom', {
        date: moment(effectiveDate).format('ll'),
      });
};

export const shouldShowDateText = (group: CertificationGroup): boolean => {
  const currentDate = new Date();
  const groupEffectiveDate = new Date(group.effectiveDate);

  // Don't show date text if effective date is in the future (unless infinite)
  if (groupEffectiveDate > currentDate && groupEffectiveDate.getFullYear() < 9999) {
    return false;
  }

  return true;
};

export const validateCertificationGroups = (
  certificationGroups: CertificationGroup[],
  allCertifications: CertificationItem[],
): CertificationGroup[] => {
  const requiredGroups: CertificationGroup[] = [];

  // Create a set of certification IDs that the user has
  const userCertificationIds = new Set(allCertifications.map(cert => cert.certification.id));

  certificationGroups.forEach(group => {
    const { validationType, exemptedCertifications } = group;

    // Skip if validation type is 1 (optional)
    if (validationType === 1) {
      return;
    }

    // Get exempted certification IDs from the group
    const exemptedCertificationIds = new Set(
      (exemptedCertifications || []).map((exempted: any) => (typeof exempted === 'string' ? exempted : exempted.id)),
    );

    // Filter out exempted certifications from the group's certifications
    const nonExemptedCertifications = group.certifications.filter(cert => !exemptedCertificationIds.has(cert.id));

    // If all certifications in the group are exempted, skip this group
    if (nonExemptedCertifications.length === 0) {
      return;
    }

    // Check which non-exempted certifications from this group the user has
    const nonExemptedCertificationIds = nonExemptedCertifications.map(cert => cert.id);
    const userHasFromGroup = nonExemptedCertificationIds.filter(id => userCertificationIds.has(id));

    if (validationType === 3) {
      // All non-exempted certifications are mandatory
      if (userHasFromGroup.length < nonExemptedCertificationIds.length) {
        // Filter out certifications the user already has from non-exempted list
        const missingCertifications = nonExemptedCertifications.filter(cert => !userCertificationIds.has(cert.id));
        requiredGroups.push({
          ...group,
          certifications: missingCertifications,
        });
      }
    } else if (validationType === 2) {
      // At least one non-exempted certification is mandatory
      if (userHasFromGroup.length === 0) {
        // User doesn't have any non-exempted certification from this group
        requiredGroups.push({
          ...group,
          certifications: nonExemptedCertifications,
        });
      }
    }
  });

  return requiredGroups;
};

export const isCertificationExempted = (
  certificationId: string,
  certificationGroups: CertificationGroup[],
): boolean => {
  return certificationGroups.some(group => {
    const exemptedCertificationIds = (group.exemptedCertifications || []).map((exempted: any) =>
      typeof exempted === 'string' ? exempted : exempted.id,
    );
    return exemptedCertificationIds.includes(certificationId);
  });
};

export const getMarqueeTextFromRequiredGroups = (
  requiredGroups: CertificationGroup[],
  t: (key: string) => string,
): string => {
  if (requiredGroups.length === 0) return '';

  const messages = requiredGroups.map(group => {
    const prefix =
      group.validationType === 3 ? t('certifications.warning.allThe') : t('certifications.warning.atLeastOneOfThe');
    return `${t('certifications.warning.pleaseAdd')} ${prefix} "${group.name}" ${t('certifications.warning.certifications')}`;
  });

  return messages.join(' • ');
};
